import React, { useEffect, useState } from 'react';

import './styles/footer.scss';
import Logo from '@components/logo/logo';
import Socials from '@components/socials/socials';
import FooterLinksGroup from './footer-links-group/footer-links-group';
import cx from 'classnames';

import { t } from '@i18n/i18n';
import footerTranslations from '@i18n/translations/components/footer/footer.json';
import LangA from '../../i18n/LangA';
import { useI18nInitNamespace } from '../../i18n/useI18nInitNamespace';
import useCurrentLanguage from '../../i18n/useCurrentLanguage';
import { relativeSupportEmail } from '../../i18n/environments';
import CloudflareImage from '../cloudflare-image/CloudflareImage';
import FooterLanguage from './footer-language.js';

const Footer = ({ isSmallFooter = false }) => {
  useI18nInitNamespace('footer', footerTranslations, true);
  const { isEs } = useCurrentLanguage();

  const [isMobile, setIsMobile] = useState(false);

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 1024);
  };
  useEffect(() => {
    handleWindowSizeChange();
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  const footerGroups = [
    {
      id: 1,
      title: t('title_Company'),
      titleId: 'footer_company',
      links: [
        {
          href: '/zerobounce-team',
          text: t('text_About_Us'),
          alt: t('ZeroBounce_Team'),
          id: 'footer_about_us',
        },

        {
          href: '/email-validation-pricing',
          text: t('Pricing'),
          alt: t('Pricing'),
          id: 'footer_pricing',
        },
        {
          href: '/partners',
          text: t('text_Partners'),
          alt: t('ZeroBounce_Partnership'),
          id: 'footer_partners',
        },
        {
          href: 'https://www.zerobounce.net/blog/',
          text: t('text_ZeroBounce_Blog'),
          external: true,
          follow: true,
          id: 'footer_blog',
        },
        {
          href: '/free-email-verifier/',
          text: t('email_verifier'),
          alt: t('email_verifier'),
          id: 'footer_email_verifier',
        },
        {
          href: '/feature-request',
          text: t('feature_request'),
          alt: t('feature_request'),
          id: 'footer_feature_request',
        },
        {
          href: '/updates',
          text: t('zb_updates'),
          alt: t('zb_updates'),
          id: 'footer_updates',
        },
        {
          href: '/email-verification-tool-reviews',
          text: t('reviews'),
          alt: t('reviews'),
          id: 'footer_email_verification_reviews',
        },
      ],
    },
    {
      id: 2,
      title: t('title_Legal'),
      alt: t('Comply_with'),
      titleId: 'footer_legal',
      links: [
        {
          href: '/privacy-policy',
          text: t('text_Privacy_Policy'),
          tab: false,
          alt: t('ZeroBounce_CA'),
          id: 'footer_privacy',
        },
        {
          href: '/terms-of-use',
          text: t('text_Terms'),
          tab: 'terms',
          alt: t('ZeroBounce_Terms'),
          id: 'footer_terms',
        },
        {
          href: '/sitemap.html',
          text: t('text_Sitemap'),
          alt: t('ZeroBounce_Sitemap'),
          id: 'footer_sitemap',
        },
        {
          href: '/ca-privacy',
          text: t('text_CA_Privacy'),
          alt: t('ZeroBounce_Privacy_Policy'),
          id: 'footer_privacy_policy',
        },
        {
          href: '/gdpr.html',
          text: t('text_GDPR'),
          alt: t('How_Is_ZeroBounce'),
          id: 'footer_gdpr',
        },
        {
          href: '/security/',
          text: t('text_Security'),
          alt: t('ZeroBounce_Security'),
          id: 'footer_security',
        },
      ],
    },
  ];

  return (
    <footer className="footer">
      <div className="shell-alt">
        <div className="footer__inner">
          <div className="footer__aside">
            <Logo id="footer_zb_logo" />

            <div className="copyright hidden-sm hidden-xs">
              <p id="footer_rights_reserved">
                &copy; {new Date().getFullYear()} ZeroBounce. <br /> {t('p_All_rights_reserved')}
              </p>
              {!isSmallFooter && (
                <a href="https://apps.apple.com/app/zerobounce/id1671793296" target="_blank" title="Apple App Store" rel="noreferrer">
                  <CloudflareImage
                    id="footer_appleLogo"
                    src={isEs ? '/images/app-store-spanish.webp' : '/images/app-store.webp'}
                    className="appleLogo"
                    alt="Apple App Store Logo"
                    width="160"
                    height="48"
                  />
                </a>
              )}
            </div>
          </div>
          {!isSmallFooter && (
            <div className="footer__content">
              {footerGroups.map((group) => (
                <FooterLinksGroup key={group.id} title={group.title} alt={group.alt} links={group.links} titleId={group.titleId}>
                  <ul>
                    {group.links &&
                      group.links.map((link) => (
                        <li key={link.text}>
                          {link.external ? (
                            <LangA id={link.id} href={link.href} title={link.alt} rel={link.follow ? '' : 'nofollow noopener noreferrer'}>
                              {link.text}
                            </LangA>
                          ) : (
                            <LangA id={link.id} href={link.href} title={link.alt}>
                              {link.boldText && <strong>{link.text}</strong>}
                              {!link.boldText && link.text}
                            </LangA>
                          )}
                        </li>
                      ))}
                  </ul>
                </FooterLinksGroup>
              ))}

              <div className="footer__contact">
                <FooterLinksGroup title={t('title_CONTACT_US')} titleId="footer_contact_us">
                  <ul>
                    <li id="footer_us_contact">
                      {t('li_US')}{' '}
                      <LangA id="footer_us_contact_number" href="tel:+18885009521">
                        1 888 500 9521
                      </LangA>
                    </li>

                    <li id="footer_eu_contact">
                      {t('li_EU')}{' '}
                      <LangA id="footer_eu_contact_number" href="tel:+443308084814">
                        +44 330 808 4814
                      </LangA>
                    </li>

                    <li id="footer_support_guaranteed">24/7 {t('li_Support_Guaranteed')}</li>

                    <li>
                      <LangA id="footer_mail_to" href={'mailto:' + relativeSupportEmail}>
                        {relativeSupportEmail}
                      </LangA>
                    </li>
                    <li>
                      <LangA id="footer_contact_us" href="/contact-us" title={t('Were_here')}>
                        {t('text_Contact')}
                      </LangA>
                    </li>
                  </ul>
                </FooterLinksGroup>

                <Socials />
              </div>
            </div>
          )}
          {isMobile && !isSmallFooter && <FooterLanguage />}

          <div className={cx('copyright visible-sm-block visible-xs-block', { 'copyright-small-footer': isSmallFooter })}>
            <p id="footer_mobile_rights_reserved">
              &copy; {new Date().getFullYear()} ZeroBounce. <br /> {t('p_All_rights_reserved')}
            </p>
            {!isSmallFooter && (
              <a href="https://apps.apple.com/app/zerobounce/id1671793296" target="_blank" title="Apple App Store" rel="noreferrer">
                <CloudflareImage
                  id="footer_mobile_apple_logo"
                  src={isEs ? '/images/app-store-spanish.webp' : '/images/app-store.webp'}
                  className="appleLogo"
                  width="160"
                  height="48"
                  alt="Apple App Store Logo"
                />
              </a>
            )}
          </div>
        </div>
        {!isMobile && !isSmallFooter && <FooterLanguage />}
      </div>
    </footer>
  );
};

export default Footer;
